/**
 * Modal xác thực tăng cường – Nhập Mã PIN Quản trị (PCI DSS 4.0.1 / ISO 27001).
 * Hỗ trợ OWASP: rate limiting – khi bị khóa (5 lần sai) hiển thị thông báo, gọi onFailedAttempt khi sai.
 * 
 * Design: Dark Glassmorphism – consistent with platform design language.
 */

import React, { useState } from 'react';
import { UserCheck, ShieldAlert, Lock, KeyRound } from 'lucide-react';

export default function StepUpModal({ isOpen, title, subtitle, onConfirm, onCancel, expectedPin, emptyMessage, isLocked, lockRemainingMs, onFailedAttempt }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    setError('');
    const trimmed = (pin || '').trim();
    if (!expectedPin) {
      setError(emptyMessage || 'Chưa cấu hình mã PIN Quản trị. Vào Cài đặt Bảo mật.');
      return;
    }
    if (trimmed === expectedPin) {
      onConfirm?.();
      setPin('');
    } else {
      setError('Mã PIN không đúng.');
      onFailedAttempt?.();
    }
  };

  const handleClose = () => {
    setPin('');
    setError('');
    onCancel?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') handleClose();
  };

  if (!isOpen) return null;

  const formatRemainingTime = (ms) => {
    if (ms == null || ms <= 0) return "15:00";
    const totalSeconds = Math.ceil(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const lockedTimeDisplay = formatRemainingTime(lockRemainingMs);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="step-up-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={handleClose} />

      {/* Modal Card */}
      <div className="relative max-w-sm w-full entrance-glass rounded-[2rem] border border-white/10 shadow-2xl p-8 overflow-hidden">
        {/* Top accent glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-24 bg-indigo-500/15 blur-[60px] pointer-events-none" />

        {/* Header */}
        <div className="relative flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center shadow-lg shadow-indigo-500/10">
            <KeyRound className="text-indigo-400" size={26} />
          </div>
          <div>
            <h2 id="step-up-title" className="text-lg font-black text-white tracking-tight">
              {title || 'Xác thực bước hai'}
            </h2>
            <p className="text-sm text-slate-400 font-medium">
              {subtitle || 'Nhập mã PIN Quản trị để tiếp tục.'}
            </p>
          </div>
        </div>

        {isLocked ? (
          /* Locked State */
          <div className="py-5 px-5 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 border border-amber-500/30">
              <ShieldAlert className="text-amber-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-amber-300 font-bold mb-1">Khóa bảo mật</p>
              <p className="text-xs text-amber-400/70 leading-relaxed">
                Đã khóa do nhập sai quá 5 lần (OWASP – chống brute force). Vui lòng thử lại sau <strong className="text-amber-300 font-mono text-sm px-1">{lockedTimeDisplay}</strong>. Chủ quán đã nhận thông báo.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* PIN Input */}
            <div className="relative mb-4">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <Lock className="text-slate-500" size={18} />
              </div>
              <input
                type="password"
                inputMode="numeric"
                maxLength={8}
                value={pin}
                onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
                onKeyDown={handleKeyDown}
                placeholder="Mã PIN"
                autoFocus
                className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-900/60 border border-white/10 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none text-white font-mono text-lg tracking-[0.5em] placeholder:text-slate-600 placeholder:tracking-normal placeholder:font-sans placeholder:text-sm transition-all"
                autoComplete="off"
              />
            </div>

            {error && (
              <div className="mb-4 px-4 py-2.5 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                <p className="text-xs text-rose-400 font-bold">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3.5 min-h-[48px] rounded-2xl bg-slate-800/80 border border-white/10 text-slate-300 font-bold text-sm uppercase tracking-wider touch-manipulation transition-all active:scale-95 hover:bg-slate-700/80 hover:text-white"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="flex-1 py-3.5 min-h-[48px] rounded-2xl bg-indigo-600 border border-indigo-500/50 text-white font-bold text-sm uppercase tracking-wider touch-manipulation transition-all active:scale-95 hover:bg-indigo-500 shadow-lg shadow-indigo-600/25"
              >
                Xác nhận
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
